import { provider } from "@/provider.js"
import Vue from "vue"
import { handleActionError } from "../../errors"
import { logFeature } from "@/helpers/featureActivity"

export default {
  async requestCurrentUser({ commit, rootState }) {
    try {
      const currentUser = await provider.findCurrentUser()
      if (!currentUser) return
      commit("receiveCurrentUser", currentUser)
      commit("setUserPermissions", currentUser.permissions || [])
      commit("setSavedSearches", currentUser.account && currentUser.account.savedSearches ? currentUser.account.savedSearches : [])
      commit("setScreenLayouts", currentUser.screenLayouts || [])
      commit("setAssignedTasks", currentUser.assignedTasks || [])
      commit("setSubscriptions", currentUser.eventSubscriptions || [])
      commit("setAccountDetails", currentUser.account || {})
      const caseTypes = currentUser.account && currentUser.account.caseTypes ? currentUser.account.caseTypes : []
      commit("setCaseTypes", caseTypes)
      commit("reloadConfigMenu", caseTypes)
      // console.log('enabling during requestCurrentUser');
      // commit('enableTeamFeatures', {currentUser, teams: rootState.teams.all})
    } catch (error) {
      handleActionError(error, commit)
    }
  },
  async refreshRecentCases({ state, commit }) {
    commit("setRecentCasesLoading", true)
    const data = await provider.refreshRecentCases()
    commit("updateRecentCases", { cases: data.recentCases, caseTypes: data.account.caseTypes })
    commit("setRecentCasesLoading", false)
  },
  async refreshNotifications({ commit }) {
    try {
      const notifications = await provider.refreshNotifications()
      commit("setNotifications", notifications)
      commit("setupRecentNotifications", notifications)
      commit("setupCaseNotifications", notifications)
    } catch (error) {
      handleActionError(error, commit)
    }
  },
  async subscribeToNotifications({ commit, state, dispatch }) {
    try {
      // Add a small delay to ensure WebSocket connection is established
      // Other subscriptions might be set up first, so we wait a bit
      await new Promise(resolve => setTimeout(resolve, 100))
      
      console.log("subscribeToNotifications action", "Setting up subscription")
      const subscription = provider.subscribeToNotifications((notification) => {
        console.log("subscribeToNotifications action", "Received notification callback", notification)
        // Add the new notification to the beginning of the list
        const currentNotifications = state.notifications || []
        const updatedNotifications = [notification, ...currentNotifications]
        commit("setNotifications", updatedNotifications)
        commit("setupRecentNotifications", updatedNotifications)
        commit("setupCaseNotifications", updatedNotifications)
      })
      
      if (!subscription) {
        console.error("subscribeToNotifications action", "Failed to create subscription")
        // Retry after a delay
        setTimeout(() => {
          dispatch("subscribeToNotifications")
        }, 2000)
      } else {
        console.log("subscribeToNotifications action", "Subscription created successfully")
      }
    } catch (error) {
      console.error("subscribeToNotifications action", "Error setting up subscription", error)
      handleActionError(error, commit)
      // Retry after a delay
      setTimeout(() => {
        dispatch("subscribeToNotifications")
      }, 2000)
    }
  },
  async unsubscribeFromNotifications() {
    try {
      provider.unsubscribeFromNotifications()
    } catch (error) {
      console.error("Error unsubscribing from notifications", error)
    }
  },
  async reloadAccount({ commit }) {
    const data = await provider.reloadAccount()
    if (data && data.account) {
      commit("setAccountDetails", data.account)
    }
  },
  async reloadTeamsAndUsers({ commit, rootState }) {
    try {
      const data = await provider.reloadTeamsAndUsers()
      commit("enableTeamFeatures", { currentUser: rootState.currentUser.me, teams: data.account.teams })
      commit("setTeams", data.account.teams)
      commit("setUsers", data.account.users)
      commit("reloadCaseWorkMenu", { caseTypes: rootState.caseTypes.all, bookmarks: rootState.currentUser.bookmarks, savedSearches: rootState.currentUser.account.savedSearches })
    } catch (error) {
      handleActionError(error, commit)
    }
  },
  async reloadAssignedTasks({ commit }) {
    const currentUser = await provider.reloadAssignedTasks()
    commit("setAssignedTasks", currentUser.assignedTasks)
    commit("setTeamAssignedTasks", currentUser.account.teams)
  },
  async reloadCaseTypes({ commit, rootState }) {
    try {
      const data = await provider.reloadCaseTypes()
      const caseTypes = (data.account && data.account.caseTypes && Array.isArray(data.account.caseTypes))
        ? data.account.caseTypes
        : []

      commit("setCaseTypes", caseTypes)
      commit("reloadCaseWorkMenu", { caseTypes, bookmarks: rootState.currentUser.bookmarks, savedSearches: rootState.currentUser.account.savedSearches })
      commit("reloadConfigMenu", caseTypes)
    } catch (error) {
      console.error(error)
      handleActionError(error, commit)
    }
  },
  async searchSavedSearch({ commit }, searchData) {
    const result = await provider.searchSavedSearch(searchData)
    commit("setSavedSearchResults", { savedSearchId: searchData.savedSearch.id, contents: result.contents })
  },
  async reloadSavedSearches({ commit }, pageData) {
    const data = await provider.reloadSavedSearches(pageData)
    commit("setSavedSearches", data.savedSearches)
  },
  async reloadSavedSearchesTotals({ commit, rootState }, pageData) {
    const data = await provider.reloadSavedSearchesTotals(pageData)
    commit("setSavedSearches", data.savedSearches)
    commit("reloadCaseWorkMenu", { caseTypes: rootState.caseTypes.all, bookmarks: rootState.currentUser.bookmarks, savedSearches: data.savedSearches })
  },
  async createSavedSearch({ state, commit }, savedSearch) {
    const response = await provider.createSavedSearch(savedSearch)
    if (response.error) {
      commit("displayFlashMessage", { message: response.message, severity: "error" })
    } else {
      commit("addSavedSearch", { savedSearch: response.savedSearch })
      commit("reloadCommsRoomItems", { newItems: state.account.savedSearches })
      return response.savedSearch
    }
  },
  async renameSavedSearch({ state, commit }, { savedSearchId, newSearchName }) {
    const response = await provider.renameSavedSearch(savedSearchId, newSearchName)
    if (response.error) {
      commit("displayFlashMessage", { message: response.message, severity: "error" })
    } else {
      commit("renameSavedSearch", { savedSearchId, newSearchName })
      commit("reloadCommsRoomItems", { newItems: state.account.savedSearches })
      commit("displayFlashMessage", { message: response.message, severity: "success" })
    }
    return !response.error
  },
  async deleteSavedSearch({ state, commit }, { savedSearchId, defaultId }) {
    const response = await provider.deleteSavedSearch(savedSearchId)
    if (response.error) {
      commit("displayFlashMessage", { message: response.message, severity: "error" })
    } else {
      Vue.router.push({
        name: "CommsInbox",
        params: { saved_search_id: defaultId },
      })
      commit("removeSavedSearch", { savedSearchId })
      commit("reloadCommsRoomItems", { newItems: state.account.savedSearches })
      commit("displayFlashMessage", { message: response.message, severity: "success" })
    }
    return !response.error
  },
  async requestLogin({ commit, dispatch }, cred) {
    commit("clearFlashMessage")
    const { token, refreshToken, error, mfaPending } = await provider.getToken(cred)
    const nextPath = cred && cred.next && typeof cred.next === "string" ? cred.next : null
    if (!error && token) {
      commit("updateSession", { token, refreshToken })
      if (mfaPending) {
        const q = nextPath ? { next: nextPath } : {}
        Vue.router.push({ path: "/login/mfa", query: q })
      } else {
        await dispatch("requestCurrentUser")
        if (nextPath) {
          Vue.router.push(nextPath)
        } else {
          Vue.router.push("/dashboard/overview")
        }
      }
    } else {
      const flashMessage = error ? error.message : "Unable to login, please check your credentials"
      commit("updateSession", undefined)
      commit("displayFlashMessage", { message: flashMessage, severity: "error" })
    }
  },
  async completeSsoLogin({ commit, dispatch }, payload) {
    commit("clearFlashMessage")
    const token = payload && payload.accessToken
    const refreshToken = payload && payload.refreshToken
    const mfaPending = payload && payload.mfaPending
    const nextPath = payload && payload.next && typeof payload.next === "string" ? payload.next : null
    if (!token) {
      commit("displayFlashMessage", { message: "SSO sign-in could not be completed.", severity: "error" })
      Vue.router.replace({ name: "Login" })
      return { error: true }
    }
    commit("updateSession", { token, refreshToken })
    if (mfaPending) {
      const q = nextPath ? { next: nextPath } : {}
      Vue.router.replace({ path: "/login/mfa", query: q })
      return { error: false }
    }
    await dispatch("requestCurrentUser")
    if (nextPath) {
      Vue.router.replace(nextPath)
    } else {
      Vue.router.replace("/dashboard/overview")
    }
    return { error: false }
  },
  async requestMFALogin({ commit, dispatch }, payload) {
    commit("clearFlashMessage")
    const totp = typeof payload === "string" ? payload : payload && payload.totp
    const nextPath =
      payload && typeof payload === "object" && payload.next && typeof payload.next === "string"
        ? payload.next
        : null
    const { token, refreshToken, error, mfaPending } = await provider.verifyTOTP(totp)
    console.log({ token, error, mfaPending })
    if (!error && token) {
      commit("updateSession", { token, refreshToken })
      if (mfaPending) {
        const q = nextPath ? { next: nextPath } : {}
        Vue.router.push({ path: "/login/mfa", query: q })
      } else {
        await dispatch("requestCurrentUser")
        if (nextPath) {
          Vue.router.push(nextPath)
        } else {
          Vue.router.push("/dashboard/overview")
        }
      }
    } else {
      const flashMessage = error ? error.message : "Unable to login, please check your credentials"
      commit("displayFlashMessage", { message: flashMessage, severity: "error" })
    }
  },
  async toggleSaveWarnings(toggle) {
    // TODO
    console.log("Toggle save warnings, Skip in future? " + toggle)
  },
  async createServiceLevelCriteria({ commit }, sla) {
    const response = await provider.createServiceLevelCriteria(sla)
    if (response.error) {
      commit("displayFlashMessage", { message: response.message, severity: "error" })
    } else {
      commit("displayFlashMessage", { message: response.message, severity: "success" })
      commit("addNewSla", response)
    }
    return response
  },
  async updateServiceLevelCriteria({ commit }, sla) {
    const response = await provider.updateServiceLevelCriteria(sla)
    if (response.error) {
      commit("displayFlashMessage", { message: response.message, severity: "error" })
    } else {
      commit("displayFlashMessage", { message: response.message, severity: "success" })
    }
    return response
  },
  async deleteServiceLevelCriteria({ commit }, slaId) {
    const response = await provider.deleteServiceLevelCriteria(slaId)
    if (response.error) {
      commit("displayFlashMessage", { message: response.message, severity: "error" })
    } else {
      commit("displayFlashMessage", { message: response.message, severity: "success" })
      commit("removeSla", slaId)
    }
    return response
  },
  async createTasklistTemplate({ commit, dispatch }, tasklistData) {
    commit("taskStarted")
    const response = await provider.createTasklistTemplate(tasklistData)
    if (response.error) {
      commit("taskUpdate", response)
    } else {
      dispatch("requestCurrentUser")
      delete response.error
      delete response.message
      delete response.lowLevelErrorMessage
    }

    commit("taskComplete")
    return response
  },
  async updateTasklistTemplate({ commit, dispatch }, tasklistData) {
    commit("taskStarted")
    const response = await provider.updateTasklistTemplate(tasklistData)
    dispatch("requestCurrentUser")
    commit("taskUpdate", response)
    commit("taskComplete")
    return response
  },
  async deleteTasklistTemplate({ commit, dispatch }, tasklistId) {
    commit("taskStarted")
    const response = await provider.deleteTasklistTemplate(tasklistId)
    if (response.error) {
      commit("taskUpdate", response)
    }
    dispatch("requestCurrentUser")
    commit("taskComplete")
    return response
  },
  async createTaskTemplate({ commit }, taskData) {
    commit("taskStarted")
    const response = await provider.createTaskTemplate(taskData)
    if (response.error) {
      commit("taskUpdate", response)
    } else {
      delete response.error
      delete response.message
      delete response.lowLevelErrorMessage
    }
    commit("taskComplete")
    return response
  },
  async updateTaskTemplate({ commit }, taskData) {
    commit("taskStarted")
    const response = await provider.updateTaskTemplate(taskData)
    commit("taskUpdate", response)
    commit("taskComplete")
    return response
  },
  async deleteTaskTemplate({ commit }, taskId) {
    commit("taskStarted")
    const response = await provider.deleteTaskTemplate(taskId)
    if (response.error) {
      commit("taskUpdate", response)
    }
    commit("taskComplete")
    return response
  },
  async createDirectRelationship({ commit }, relationData) {
    commit("taskStarted")
    const response = await provider.createDirectRelationship(relationData)
    if (response.error) {
      commit("taskUpdate", response)
    } else {
      delete response.error
      delete response.message
      delete response.lowLevelErrorMessage
    }
    commit("taskComplete")
    return response
  },
  async updateDirectRelationship({ commit }, relationData) {
    commit("taskStarted")
    const response = await provider.updateDirectRelationship(relationData)
    commit("taskUpdate", response)
    commit("taskComplete")
    return response
  },
  async deleteDirectRelationship({ commit }, taskId) {
    commit("taskStarted")
    const response = await provider.deleteDirectRelationship(taskId)
    if (response.error) {
      commit("taskUpdate", response)
    }
    commit("taskComplete")
    return response
  },
  async createDirectRelationshipDisplayField({ commit }, field) {
    commit("taskStarted")
    const response = await provider.createDirectRelationshipDisplayField(field)
    if (response.error) {
      commit("taskUpdate", response)
    }
    commit("taskComplete")
    return response
  },
  async deleteDirectRelationshipDisplayField({ commit }, data) {
    commit("taskStarted")
    const response = await provider.deleteDirectRelationshipDisplayField(data)
    if (response.error) {
      commit("taskUpdate", response)
    } else {
      commit("deleteRelationshipDisplayField", data)
    }
    commit("taskComplete")
    return response
  },
  async executeTask({ commit }, taskData) {
    commit("taskStarted")
    const response = await provider.executeTask(taskData)
    commit("taskUpdate", response)
    commit("taskComplete")
    return response
  },
  async createTaskTemplateParameter({ commit }, taskData) {
    commit("taskStarted")
    const response = await provider.createTaskTemplateParameter(taskData)
    if (response.error) {
      commit("taskUpdate", response)
    } else {
      delete response.error
      delete response.message
      delete response.lowLevelErrorMessage
    }
    commit("taskComplete")
    return response
  },
  async updateTaskTemplateParameter({ commit }, taskData) {
    commit("taskStarted")
    const response = await provider.updateTaskTemplateParameter(taskData)
    commit("taskUpdate", response)
    commit("taskComplete")
    return response
  },
  async deleteTaskTemplateParameter({ commit }, taskId) {
    commit("taskStarted")
    const response = await provider.deleteTaskTemplateParameter(taskId)
    if (response.error) {
      commit("taskUpdate", response)
    }
    commit("taskComplete")
    return response
  },

  async createSummaryDisplayField({ commit }, displayField) {
    commit("taskStarted")
    const response = await provider.createSummaryDisplayField(displayField)
    if (response.error) {
      commit("taskUpdate", response)
    } else {
      delete response.error
      delete response.message
      delete response.lowLevelErrorMessage
    }
    commit("taskComplete")
    return response
  },
  async updateSummaryDisplayField({ commit }, displayField) {
    commit("taskStarted")
    const response = await provider.updateSummaryDisplayField(displayField)
    commit("taskUpdate", response)
    commit("taskComplete")
    return response
  },

  summariseCases(_, caseTypeId) {
    provider.summariseCases(caseTypeId)
  },
  async deleteSummaryDisplayField({ commit }, id) {
    commit("taskStarted")
    const response = await provider.deleteSummaryDisplayField(id)
    if (response.error) {
      commit("taskUpdate", response)
    }
    commit("taskComplete")
    return response
  },

  async createCaseState({ commit }, stateData) {
    commit("taskStarted")
    const response = await provider.createCaseState(stateData)
    if (response.error !== null) {
      commit("taskUpdate", response)
    } else {
      delete response.message
      delete response.error
    }
    commit("taskComplete")
    return response
  },
  async updateCaseState({ commit }, stateData) {
    commit("taskStarted")
    const response = await provider.updateCaseState(stateData)
    commit("taskUpdate", response)
    commit("taskComplete")
    return response
  },
  async deleteCaseState({ commit }, stateId) {
    commit("taskStarted")
    const response = await provider.deleteCaseState(stateId)
    if (response.error) {
      commit("taskUpdate", response)
    } else {
      delete response.error
      delete response.message
    }
    commit("taskComplete")
    return response
  },

  async addParticipantsToCase({ commit }, { participants, type, caseId }) {
    if (participants && participants.length > 0) {
      let error = false
      let message = ""
      for (const participant of participants) {
        const result = await provider.addCaseMember(caseId, participant.id, type)
        if (result.addCaseParticipant.error) {
          error = true
          message = result.addCaseParticipant.message
        }
      }
      if (error) {
        commit("displayFlashMessage", { message: message, severity: "error" })
      }
    }
  },
  async removeParticipantsFromCase({ commit }, { participants, type, caseId }) {
    if (participants && participants.length > 0) {
      let error = false
      let message = ""
      for (const participant of participants) {
        const result = await provider.removeCaseMember(caseId, participant.id, type)
        if (result.removeCaseParticipant.error) {
          error = true
          message = result.removeCaseParticipant.message
        }
      }
      if (error) {
        commit("displayFlashMessage", { message: message, severity: "error" })
      }
    }
  },
  async updateQuestions({ commit }, data) {
    commit("taskStarted")
    const response = await provider.updateQuestions(data)
    commit("taskUpdate", response)
    commit("taskComplete")
    return response
  },
  async updateQuestionSets({ commit }, data) {
    commit("taskStarted")
    const response = await provider.updateQuestionSets(data)
    commit("taskUpdate", response)
    commit("taskComplete")
    return response
  },
  async grantPermission({ commit }, permData) {
    commit("taskStarted")
    const response = await provider.grantPermission(permData)
    if (response.error) {
      commit("taskUpdate", response)
    } else {
      delete response.error
      delete response.message
      delete response.lowLevelErrorMessage
      delete response.__typename
      response.resource = {
        id: response.resourceId,
        name: permData.resource,
      }
      response.team = {
        id: response.teamId,
      }
      delete response.resourceId
      delete response.teamId
    }
    commit("taskComplete")
    return response
  },
  async revokePermission({ commit }, permData) {
    commit("taskStarted")
    const response = await provider.revokePermission(permData)
    if (response.error) {
      commit("taskUpdate", response)
    }
    commit("taskComplete")
    return response
  },
  async grantFieldsetPermission({ commit }, permData) {
    commit("taskStarted")
    const response = await provider.grantFieldsetPermission(permData)
    if (response.error) {
      commit("taskUpdate", response)
    } else {
      delete response.error
      delete response.message
      delete response.lowLevelErrorMessage
      delete response.__typename
      response.resource = {
        id: permData.fieldset.id,
        name: permData.fieldset.name,
        permissionType: "read_write_hidden",
        resourceType: "case",
      }
      response.team = {
        id: permData.teamId,
      }
      delete response.resourceId
      delete response.teamId
    }
    commit("taskComplete")
    return response
  },
  async revokeFieldsetPermission({ commit }, permData) {
    commit("taskStarted")
    const response = await provider.revokeFieldsetPermission(permData)
    if (response.error) {
      commit("taskUpdate", response)
    }
    commit("taskComplete")
    return response
  },
  async grantTasklistPermission({ commit }, permData) {
    commit("taskStarted")
    const response = await provider.grantTasklistPermission(permData)
    if (response.error) {
      commit("taskUpdate", response)
    } else {
      delete response.error
      delete response.message
      delete response.lowLevelErrorMessage
      delete response.__typename
      response.resource = {
        id: permData.resourceId,
        name: permData.tasklistName,
        permissionType: "read_write_hidden",
        resourceType: "case",
      }
      response.team = {
        id: permData.teamId,
      }
      delete response.resourceId
      delete response.teamId
    }
    commit("taskComplete")
    return response
  },
  async revokeTasklistPermission({ commit }, permData) {
    commit("taskStarted")
    const response = await provider.revokeTasklistPermission(permData)
    if (response.error) {
      commit("taskUpdate", response)
    }
    commit("taskComplete")
    return response
  },
  async updateExistingCases({ commit }, id) {
    // Updates existing cases of a given caseType id
    commit("taskStarted")
    const response = await provider.updateExistingCases(id)
    commit("taskUpdate", response)
    commit("taskComplete")
  },
  async updateExistingTasks({ commit }, id) {
    // Updates existing tasks of a given caseType id
    commit("taskStarted")
    const response = await provider.updateExistingTasks(id)
    commit("taskUpdate", response)
    commit("taskComplete")
  },
  async mockUser({ commit }, userData) {
    commit("receiveCurrentUser", userData.currentUser)
    commit("setCaseTypes", userData.caseTypes)
  },
  updateCurrentUserPageSize({ commit }, data) {
    provider.updateUserPreferences({ id: data.id, userPreferences: data.preferences })
    commit("updateCurrentUserPageSize", data.preferences.pageSize)
  },
  async setSearchExperience({ state, commit }, enabled) {
    const me = state.me
    const prefs = me.preferences || {}
    const timeline = prefs.timeline || {}
    const previous = prefs.unifiedSearch
    commit("setUnifiedSearch", enabled)
    try {
      await provider.updateUserPreferences({
        id: me.id,
        userPreferences: {
          dateTimeFormat: prefs.dateTimeFormat,
          dateFormat: prefs.dateFormat,
          pageSize: prefs.pageSize || 10,
          unifiedSearch: enabled,
          timeline: {
            updates: timeline.updates,
            viewed: timeline.viewed,
            created: timeline.created,
            participants: timeline.participants,
            conversations: timeline.conversations,
            stateChanges: timeline.stateChanges,
            tasks: timeline.tasks,
            documents: timeline.documents,
            notes: timeline.notes
          }
        }
      })
    } catch (err) {
      commit("setUnifiedSearch", previous !== false)
      commit("displayFlashMessage", { message: "Could not save your search preference", severity: "error" })
    }
  },
  async clearAllNotifications({ state, commit }) {
    const response = await provider.clearAllNotifications()
    if (response.error) {
      commit("displayFlashMessage", { message: response.message, severity: "error" })
    } else {
      state.notifications = []
      commit("setNotifications", state.notifications)
      commit("setupRecentNotifications", state.notifications)
      commit("setupCaseNotifications", state.notifications)
    }
    return response.error
  },
  async clearNotification({ state, commit }, notificationId) {
    const response = await provider.clearNotification(notificationId)
    if (response.error) {
      commit("displayFlashMessage", { message: response.message, severity: "error" })
    } else {
      if (state.notifications) {
        const ids = state.notifications.map((note) => note.id)
        const index = ids.indexOf(String(notificationId))
        state.notifications.splice(index, 1)
      }
      commit("setNotifications", state.notifications)
      commit("setupRecentNotifications", state.notifications)
      commit("setupCaseNotifications", state.notifications)
    }
    return response.error
  },
  async addNewEmailAccount({ commit, state }, { name, description = "" }) {
    // Create a minimal email account immediately so it gets an ID
    const newEmailAccount = {
      name: name,
      description: description || "",
      isDefault: false,
      sendChannel: "Office365Connector",
      receiveChannel: "Office365Connector",
      imapDetails: { host: "outlook.office365.com", port: 993, login: "", password: "", ssl: false, startTLS: true, enabled: true }, 
      smtpDetails: { host: "smtp.office365.com", port: 587, login: "", password: "", ssl: false, startTLS: true, enabled: true, replyToEmailAddress: "" }
    }
    
    const response = await provider.createEmailAccount(newEmailAccount)
    if (response.error) {
      // Check if it's a duplicate name error
      const errorMessage = response.message || response.lowLevelErrorMessage || "Unknown error"
      const isDuplicateError = errorMessage.includes("Duplicate entry") || errorMessage.includes("duplicate")
      
      if (isDuplicateError) {
        // Return error to be handled by the dialog
        throw new Error("An email account with this name already exists. Please choose a different name.")
      } else {
        commit("displayFlashMessage", { message: `Unable to create email account: ${errorMessage}`, severity: "error" })
        throw new Error(errorMessage)
      }
    } else if (response.emailAccount) {
      // Add the created email account to the state
      commit("upsertEmailAccount", response.emailAccount)
      commit("displayFlashMessage", { message: `Email account created.`, severity: "success" })
      return response.emailAccount
    }
  },
  async setDefaultTeam({ commit }, teamId) {
    const response = await provider.setDefaultTeam(teamId)
    if (response.error) {
      commit("displayFlashMessage", { message: response.message || "Unable to update the default team.", severity: "error" })
    } else {
      commit("setAccountDetails", response.account)
      commit("displayFlashMessage", { message: "Default team updated.", severity: "success" })
    }
  },
  async setAccountTimezone({ commit }, timezone) {
    const response = await provider.setAccountTimezone(timezone)
    if (response.error) {
      commit("displayFlashMessage", { message: `Unable to store account timezone.`, severity: "error" })
    } else {
      commit("setAccountDetails", response.account)
      commit("displayFlashMessage", { message: `Account Updated.`, severity: "success" })
    }
  },
  async setAccountChatBriefing({ commit }, chatBriefing) {
    const response = await provider.setAccountChatBriefing(chatBriefing)
    if (response.error) {
      commit("displayFlashMessage", { message: `Unable to store account chatBriefing.`, severity: "error" })
    } else {
      commit("setAccountDetails", response.account)
      commit("displayFlashMessage", { message: `Account Updated.`, severity: "success" })
    }
  },
  async setAccountChatReports({ commit }, chatReports) {
    const response = await provider.setAccountChatReports(chatReports)
    if (response.error) {
      commit("displayFlashMessage", { message: `Unable to store account chat reports.`, severity: "error" })
    } else {
      commit("setAccountDetails", response.account)
      commit("displayFlashMessage", { message: `Account Updated.`, severity: "success" })
    }
  },
  async setChatMemoryEnabled({ commit }, enabled) {
    const response = await provider.setChatMemoryEnabled(enabled)
    if (response.error) {
      commit("displayFlashMessage", { message: `Unable to update chat memory setting.`, severity: "error" })
    } else {
      commit("setAccountDetails", response.account)
      commit("displayFlashMessage", { message: `Account Updated.`, severity: "success" })
    }
  },
  async setAccountRefreshTokenExpiryHours({ commit, state }, refreshTokenExpiryHours) {
    const accountId = state.account && state.account.id
    if (!accountId) {
      commit("displayFlashMessage", { message: "Unable to update user session timeout right now.", severity: "error" })
      return
    }
    const response = await provider.setAccountRefreshTokenExpiryHours({ id: accountId, refreshTokenExpiryHours })
    if (response.error) {
      commit("displayFlashMessage", { message: `Unable to store refresh token timeout.`, severity: "error" })
    } else {
      commit("setAccountDetails", response.account)
      commit("displayFlashMessage", { message: `Account Updated.`, severity: "success" })
    }
  },
  async setOidcSso({ commit }, config) {
    logFeature("oidc", "persist", "Persisting account OIDC SSO settings", {
      enabled: !!(config && config.enabled),
      hasSecretUpdate: !!(config && config.clientSecret),
    })
    const response = await provider.setOidcSso(config)
    if (response.error) {
      commit("displayFlashMessage", { message: response.message || "Unable to update SSO settings.", severity: "error" })
    } else {
      if (response.account) {
        commit("setAccountDetails", response.account)
      }
      commit("displayFlashMessage", { message: response.message || "SSO settings updated.", severity: "success" })
    }
  },
  async setAccountAdminConsoleToken({ commit, state }, adminConsoleToken) {
    const accountId = state.account && state.account.id
    if (!accountId) {
      commit("displayFlashMessage", { message: "Unable to update Admin Console token right now.", severity: "error" })
      return
    }
    const response = await provider.setAccountAdminConsoleToken({ id: accountId, adminConsoleToken })
    if (response.error) {
      commit("displayFlashMessage", { message: "Unable to store Admin Console token.", severity: "error" })
    } else {
      commit("setAccountDetails", response.account)
      commit("displayFlashMessage", { message: "Account Updated.", severity: "success" })
    }
  },
  async storeEmailAccounts({ commit, state }, emailAccountChangedIndeces) {
    let stored = true
    for (const index of emailAccountChangedIndeces) {
      const changedEmailAccount = state.account.emailAccounts[index]
      let response
      if (changedEmailAccount.id) {
        response = await provider.updateEmailAccount(changedEmailAccount)
      } else {
        response = await provider.createEmailAccount(changedEmailAccount)
      }
      if (response.error) {
        stored = false
        commit("displayFlashMessage", { message: `Unable to store ${changedEmailAccount.name} email account.`, severity: "error" })
      }
    }
    if (stored) {
      commit("displayFlashMessage", { message: `Email account settings stored.`, severity: "success" })
    }
  },
  deleteEmailAccount({ commit }, { emailAccount, index }) {
    if (emailAccount.id) {
      const response = provider.deleteEmailAccount(emailAccount)
      if (response.error) {
        commit("displayFlashMessage", { message: `Unable to delete ${emailAccount.name} email account.`, severity: "error" })
      } else {
        commit("deleteEmailAccount", index)
      }
    } else {
      commit("deleteEmailAccount", index)
    }
  },
  async setupMailgunDomain({ commit }, { emailAccountId, domain }) {
    try {
      const response = await provider.setupMailgunDomain({ emailAccountId, domain })
      if (response.error) {
        commit("displayFlashMessage", { message: response.message || response.lowLevelErrorMessage || "Unable to configure Caseblocks domain.", severity: "error" })
        return response
      }
      if (response.emailAccount) {
        commit("upsertEmailAccount", response.emailAccount)
      }
      commit("displayFlashMessage", { message: response.message || "Mailgun domain updated.", severity: "success" })
      return response
    } catch (error) {
      console.error("setupMailgunDomain failed", error)
      commit("displayFlashMessage", { message: "Unable to configure Caseblocks domain. Please try again.", severity: "error" })
      throw error
    }
  },
  async deleteMessage({ commit, rootState, dispatch }, { messageId, savedSearchId }) {
    // Optimistically remove the message from the UI (after fade animation in component)
    if (savedSearchId) {
      commit("removeMessageFromSavedSearch", { savedSearchId, messageId })
    }
    
    const result = await provider.deleteMessage(messageId)
    if (result.error) {
      commit("displayFlashMessage", { message: result.message, severity: "error" })
      // If deletion failed, reload to restore the message
      if (savedSearchId) {
        const savedSearch = rootState.currentUser.account.savedSearches.find(s => parseInt(s.id) === parseInt(savedSearchId))
        const currentPage = savedSearch && savedSearch.contents && savedSearch.contents.messages
          ? Math.max(0, Math.floor(savedSearch.contents.messages.length / (rootState.currentUser.me.preferences.pageSize || 10)))
          : 0
        await dispatch("reloadSavedSearches", {
          currentPage,
          pageSize: rootState.currentUser.me.preferences.pageSize || 10
        })
      }
      return { error: true, message: result.message }
    } else {
      commit("displayFlashMessage", { message: result.message, severity: "success" })
      return { error: false, message: result.message }
    }
  },
  async systemEmailTemplates() {
    return provider.systemEmailTemplates()
  },
  async updateSystemEmailTemplate({ commit }, template) {
    const response = await provider.updateSystemEmailTemplate(template)
    if (response.error) {
      commit("displayFlashMessage", { message: response.message || "Unable to update system email.", severity: "error" })
    } else {
      commit("displayFlashMessage", { message: response.message || "System email updated.", severity: "success" })
    }
    return response
  },
  async resetSystemEmailTemplate({ commit }, id) {
    const response = await provider.resetSystemEmailTemplate(id)
    if (response.error) {
      commit("displayFlashMessage", { message: response.message || "Unable to restore system email.", severity: "error" })
    } else {
      commit("displayFlashMessage", { message: response.message || "System email restored.", severity: "success" })
    }
    return response
  },
}
